/**
 * Nordic Saw — desktop hover for header mega / dropdown menus.
 * Nested subcategories expand inside the outlined dropdown.
 */
(() => {
  const MQ = window.matchMedia(
    '(min-width: 990px) and (hover: hover) and (pointer: fine)'
  );
  const OPEN_DELAY = 500; // intent pause so a pass across the bar does not flash the catalog
  const CLOSE_DELAY = 400; // long enough to cross the gap into the panel
  const timers = new WeakMap();

  const isDesktopHover = () => MQ.matches;

  const openDetails = (details) => {
    if (!details) return;
    details.setAttribute('open', '');
    details.querySelector(':scope > summary')?.setAttribute('aria-expanded', 'true');
  };

  const closeDetails = (details) => {
    if (!details || !details.hasAttribute('open')) return;
    details.removeAttribute('open');
    details.querySelector(':scope > summary')?.setAttribute('aria-expanded', 'false');
    details.querySelectorAll('details[open]').forEach((d) => {
      d.removeAttribute('open');
      d.querySelector(':scope > summary')?.setAttribute('aria-expanded', 'false');
    });
  };

  const clearTimer = (host) => {
    const existing = timers.get(host);
    if (existing) window.clearTimeout(existing);
  };

  const bindDetailsHover = (host, details, { openDelay = 0, topLevel = false } = {}) => {
    if (!details || details.dataset.nordicHoverBound === 'true') return;
    details.dataset.nordicHoverBound = 'true';
    const summary = details.querySelector(':scope > summary');
    const panel = details.querySelector(':scope > .mega-menu__content, :scope > .header__submenu');

    const anyTopMenuOpen = () =>
      !!document.querySelector(
        '.header__inline-menu > .list-menu > li > header-menu > details[open]'
      );

    const pointerOverMenu = () =>
      !!(host.matches(':hover') || details.matches(':hover') || (panel && panel.matches(':hover')));

    const openNow = () => {
      const parent = details.parentElement;
      if (parent) {
        parent
          .querySelectorAll(':scope > details[open], :scope > header-menu > details[open], :scope > li > details[open]')
          .forEach((d) => {
            if (d !== details) closeDetails(d);
          });
      }
      if (topLevel) {
        document
          .querySelectorAll('.header__inline-menu > .list-menu > li > header-menu > details[open]')
          .forEach((d) => {
            if (d !== details) closeDetails(d);
          });
      }
      openDetails(details);
    };

    const scheduleOpen = () => {
      if (!isDesktopHover()) return;
      clearTimer(host);
      if (details.hasAttribute('open') || (topLevel && anyTopMenuOpen())) {
        openNow();
        return;
      }
      const t = window.setTimeout(() => {
        if (pointerOverMenu()) openNow();
      }, openDelay);
      timers.set(host, t);
    };

    const scheduleClose = () => {
      if (!isDesktopHover()) return;
      clearTimer(host);
      const t = window.setTimeout(() => {
        if (pointerOverMenu()) return;
        closeDetails(details);
      }, CLOSE_DELAY);
      timers.set(host, t);
    };

    host.addEventListener('mouseenter', scheduleOpen);
    host.addEventListener('mouseleave', scheduleClose);
    if (panel) {
      panel.addEventListener('mouseenter', () => {
        if (!isDesktopHover()) return;
        clearTimer(host);
        // Keep an already-open panel open; never skip the hover delay.
        if (details.hasAttribute('open')) openDetails(details);
      });
      panel.addEventListener('mouseleave', scheduleClose);
    }

    if (summary) {
      summary.addEventListener('click', (event) => {
        if (!isDesktopHover()) return;
        if (event.target.closest('a')) return;
        if (details.hasAttribute('open') && event.detail !== 0) {
          event.preventDefault();
        }
      });
    }
  };

  const bindNestedClick = (nested) => {
    if (!nested || nested.dataset.nordicNestedClick === 'true') return;
    nested.dataset.nordicNestedClick = 'true';
    nested.addEventListener('toggle', () => {
      if (!nested.open) return;
      const list = nested.closest('ul');
      list?.querySelectorAll(':scope > li > details[open]').forEach((d) => {
        if (d !== nested) {
          d.removeAttribute('open');
          d.querySelector(':scope > summary')?.setAttribute('aria-expanded', 'false');
        }
      });
    });
  };

  const bind = (menu) => {
    const details = menu.querySelector(':scope > details');
    // Top-level (including Brands): delayed hover, same as every navy category.
    bindDetailsHover(menu, details, { openDelay: OPEN_DELAY, topLevel: true });
    // Nested under any category: click-to-open only (no hover expand).
    menu.querySelectorAll('.header__submenu details').forEach(bindNestedClick);
  };

  const init = () => {
    document
      .querySelectorAll('.header__inline-menu header-menu')
      .forEach(bind);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
})();


/* First-visit sharpening welcome */
(() => {
  const root = document.getElementById('nordic-sharpen-welcome');
  if (!root) return;

  const key = root.dataset.storageKey || 'nordic_sharpen_welcome_v1';
  const delay = parseInt(root.dataset.delay || '12000', 10);

  const seen = () => {
    try {
      return window.localStorage.getItem(key) === '1';
    } catch (e) {
      return false;
    }
  };

  const mark = () => {
    try {
      window.localStorage.setItem(key, '1');
    } catch (e) {}
  };

  const close = () => {
    root.classList.remove('is-open');
    window.setTimeout(() => {
      root.hidden = true;
    }, 350);
    mark();
  };

  const open = () => {
    if (seen()) return;
    root.hidden = false;
    void root.offsetWidth;
    root.classList.add('is-open');
  };

  root.querySelectorAll('[data-nordic-sw-close]').forEach((el) => {
    el.addEventListener('click', close);
  });

  root.querySelector('.nordic-sw__cta')?.addEventListener('click', mark);

  if (!seen()) {
    window.setTimeout(open, Number.isFinite(delay) ? delay : 12000);
  }
})();
